import { fetcher, SHOW_ERROR_PAGE_ON_FETCH_FAIL } from '@features/common';

const employeeEndpoint = '/api/v1/employees/';
const employeeDocumentsEndpoint = '/api/v1/employee-documents/';
const employeeEducationsEndpoint = '/api/v1/educations/';
const employeeContactsEndpoint = '/api/v1/contacts/';
const employeePropertiesEndpoint = '/api/v1/employee-properties/';
const uploadFileEndpoint = '/api/v1/upload-file/';
const accountsAvatarEndpoint = '/api/v1/accounts/avatar/';

export function uploadEmployeeAvatar(file) {
  const formData = new FormData();
  formData.append('file', file);

  return fetcher.post(uploadFileEndpoint, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export function updateMyAvatar(payload) {
  return fetcher.patch(accountsAvatarEndpoint, payload);
}

export function fetchEmployeeList() {
  return fetcher.get(employeeEndpoint);
}

export function fetchSingleEmployee({ id }) {
  return fetcher.get(`${employeeEndpoint + id}/`);
}

// A forbidden or missing leave resource must not replace the profile with the
// global error page; the profile simply shows no leave data.
const quietFetch = { meta: { [SHOW_ERROR_PAGE_ON_FETCH_FAIL]: false } };

/**
 * Leave history and balances of an employee. Adjustment history is only
 * readable by HR managers and directors, so it is requested (and returned as
 * an array) only when `includeAdjustments` is set; otherwise it is null.
 */
export async function fetchEmployeeLeaves({ id, includeAdjustments }) {
  const [{ data: list }, { data: types }, adjustments] = await Promise.all([
    fetcher.get(`${employeeEndpoint + id}/leaves/`, quietFetch),
    fetcher.get(`${employeeEndpoint + id}/leave_types/`, quietFetch),
    includeAdjustments ? fetchLeaveAdjustments(id) : null,
  ]);

  return { list, types, adjustments };
}

// The endpoint is paginated with a page_size of at most 100.
async function fetchLeaveAdjustments(id) {
  const results = [];
  let page = 1;
  let hasNext = true;

  while (hasNext) {
    // eslint-disable-next-line no-await-in-loop
    const { data } = await fetcher.get(
      `${employeeEndpoint + id}/leave_adjustments/`,
      { ...quietFetch, params: { page, page_size: 100 } },
    );
    results.push(...(data.results || []));
    hasNext = Boolean(data.next);
    page += 1;
  }

  return results;
}

export function createEmployee(data) {
  return fetcher.post(employeeEndpoint, data);
}

export function updateEmployee(data) {
  return fetcher.patch(`${employeeEndpoint + data.id}/`, data);
}

export function removeEmployee(id) {
  return fetcher.delete(`${employeeEndpoint + id}/`);
}

export function terminateEmployee(id) {
  return fetcher.delete(`${employeeEndpoint + id}/terminate/`);
}

// Contacts
export function createEmployeeContact(contact) {
  return fetcher.post(employeeContactsEndpoint, contact);
}

export function updateEmployeeContact(contact) {
  return fetcher.put(`${employeeContactsEndpoint + contact.id}/`, contact);
}

// Documents
/**
 * Advisory offboarding check (HR managers only). Resolves to
 * `{ employeeId, requiresHrReview, bankLoanDocumentsCount, bankLoanDocuments }`.
 * A failure must not replace the profile with the global error page.
 */
export function getOffboardingCheck(employeeId) {
  return fetcher.get(`${employeeEndpoint}${employeeId}/offboarding-check/`, {
    meta: { [SHOW_ERROR_PAGE_ON_FETCH_FAIL]: false },
  });
}

export function getEmployeeDocuments(id) {
  return fetcher.get(employeeDocumentsEndpoint);
}

export function createEmployeeDocument(document) {
  return fetcher.post(employeeDocumentsEndpoint, document);
}

export function updateEmployeeDocument(document) {
  return fetcher.put(`${employeeDocumentsEndpoint + document.id}/`, document);
}

// Educations
export function createEmployeeEducation(education) {
  return fetcher.post(employeeEducationsEndpoint, education);
}

export function updateEmployeeEducation(education) {
  return fetcher.put(
    `${employeeEducationsEndpoint + education.id}/`,
    education,
  );
}

// Properties
export async function fetchStateProperties() {
  const { data: categories } = await fetcher.get(
    '/api/v1/property-categories/',
  );
  const { data: items } = await fetcher.get('/api/v1/state-properties/');

  return { categories, items };
}
export function createEmployeeProperty(property) {
  return fetcher.post(employeePropertiesEndpoint, property);
}

export function updateEmployeeProperty(property) {
  return fetcher.put(`${employeePropertiesEndpoint + property.id}/`, property);
}

export function changePassword(data) {
  return fetcher.post('/api/v1/accounts/password-change/', data);
}

export function checkUsername(username) {
  return fetcher.get(`/api/v1/accounts/check-username/`, {
    params: { username },
  });
}

/**
 * @param {{ user: number, leaveType: number, adjustment: number, effectiveDate: string, reason: string }} data
 */
export function adjustEmployeeLeave(data) {
  return fetcher.post('/api/v1/leave_adjustments/', data);
}
